import { useMemo, useEffect, useCallback } from 'react';

import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid2';
import { Card, Stack, Typography, CircularProgress } from '@mui/material';

import { channelStep1Data } from 'src/utils/constants';

import { useTranslate } from 'src/locales';
import { familiesWithAnyAllowedCapability } from 'src/pages/setting/components/channel-feature-flags/utils/channel-create-family-eligibility';

import { Image } from 'src/components/image';

import { useChannelContext } from '../hooks';
import { ChannelStepLayout } from '../shared/channel-step-layout';
import { useChannelWizardAccessContext } from '../hooks/use-channel-wizard-access-context';

export function Step1() {
  const { t } = useTranslate('translation');
  const { state: channelState, onSelectChannel, onChangeStep } = useChannelContext();
  const {
    tenantFlags,
    wizardAccessReady,
    showWizardAccessLoader,
    roleRowsForEligibility,
    isSuperAdmin,
  } = useChannelWizardAccessContext();

  const allowedFamilies = useMemo(() => {
    if (!wizardAccessReady) return null;
    return familiesWithAnyAllowedCapability(tenantFlags, roleRowsForEligibility, isSuperAdmin);
  }, [wizardAccessReady, tenantFlags, roleRowsForEligibility, isSuperAdmin]);

  const channels = useMemo(() => {
    const all = channelStep1Data(t);
    if (allowedFamilies === null) return [];
    return all.filter((item) => allowedFamilies.has(item.value));
  }, [t, allowedFamilies]);

  const handleDoubleClick = useCallback(
    (value: string) => {
      onSelectChannel(value);
      onChangeStep('next');
    },
    [onSelectChannel, onChangeStep]
  );

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const code = params.get('code');
    const stateParam = params.get('state') ?? '';

    // WHAT & WHY: Check if auth callback originates from Instagram or Facebook Messenger/WhatsApp
    // so that Facebook logins during WhatsApp edits do not post an instagram_auth_success event.
    const isInstagramAuth =
      window.location.pathname.includes('instagram') ||
      stateParam.includes('instagram') ||
      params.has('instagram');

    const successType = isInstagramAuth ? 'instagram_auth_success' : 'facebook_auth_success';
    const errorType = isInstagramAuth ? 'instagram_auth_error' : 'facebook_auth_error';

    if (window.opener) {
      window.opener.postMessage(
        code ? { type: successType, code } : { type: errorType, error: 'No code returned' },
        window.location.origin
      );
      window.close();
    }
  }, []);

  return (
    <ChannelStepLayout title="" isNextDisabled={!channelState.channelType}>
      {showWizardAccessLoader ? (
        <Stack alignItems="center" justifyContent="center" minHeight={240}>
          <CircularProgress size={36} />
        </Stack>
      ) : channels.length === 0 ? (
        <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ py: 4 }}>
          {t('NO_CHANNELS_FROM_TENANT_FLAGS')}
        </Typography>
      ) : (
        <Grid container spacing={3} justifyContent="center">
          {channels.map((data) => (
            <Grid key={data.value} size={{ xs: 12, md: 6 }}>
              <Card
                sx={{
                  p: 4,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  margin: '0 auto',
                  cursor: 'pointer',
                  width: '90%',
                  height: '150px',
                  border: channelState.channelType === data?.value ? '1px solid #00A76F' : 'none',
                }}
                onClick={() => onSelectChannel(data?.value)}
                onDoubleClick={() => handleDoubleClick(data.value)}
              >
                <Box sx={{ display: 'flex' }}>
                  <Image
                    src={data.icon}
                    alt={data.channel_name}
                    sx={{ width: '75px', height: '75px' }}
                  />
                </Box>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                  {data.channel_name}
                </Typography>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
    </ChannelStepLayout>
  );
}
